import { MarkdownText } from "@/components/markdown-text";
import { DealerLocationToolCard } from "@/components/dealer-location-tool-card";
import { VehicleStockCard } from "@/components/vehicle-stock-card";
import { SlotPicker } from "@/components/slot-picker";
import { BookingConfirmationCard } from "@/components/booking-confirmation-card";
import { TypologyCard } from "@/components/typology-card";
import { FinancingEligibilityCard } from "@/components/financing-eligibility-card";
import { LeaseCalculatorCard } from "@/components/lease-calculator-card";
import { ToolFallback } from "@/components/tool-fallback";
import { SpeakButton } from "@/components/speak-button";
import { TooltipIconButton } from "@/components/tooltip-icon-button";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  SlotPickerPayload,
  BookingConfirmationPayload,
  TypologyResult,
  FinancingEligibilityResult,
  LeaseCalculatorResult,
} from "@/lib/api";
import { messageToPlainText } from "@/lib/message-text";
import { speakText, type TtsHandle } from "@/lib/tts-player";
import { useVoiceStore } from "@/stores/voice-store";
import {
  ActionBarPrimitive,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  WebSpeechDictationAdapter,
  type ToolCallMessagePartProps,
  useAuiState,
  useThreadRuntime,
} from "@assistant-ui/react";
import {
  ArrowUpRightIcon,
  CarIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  EarIcon,
  EarOffIcon,
  Loader2Icon,
  MicIcon,
  PencilIcon,
  PlusIcon,
  SquareIcon,
  StoreIcon,
  TagIcon,
  Volume2Icon,
  VolumeXIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { toBcp47 } from "@/i18n";
import { useLocale, useT } from "@/locale-context";

export const Thread: FC = () => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col"
      style={{
        ["--thread-max-width" as string]: "44rem",
        ["--composer-radius" as string]: "26px",
        ["--composer-padding" as string]: "10px",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        className="aui-thread-viewport relative mx-3 mt-2 flex flex-1 flex-col overflow-x-hidden overflow-y-auto scroll-smooth rounded-2xl border border-blue-200 bg-white px-5 py-4"
      >
        <ThreadWelcome />

        {/* Per-message instances (rendered as a child of the last message)
            don't exist yet when the thread is empty, so the very first STT
            interaction has nowhere to surface "Listening…" / "Transcribing…".
            Mount one here as the empty-state owner; it goes away as soon as
            the first message renders and the per-message instance takes over. */}
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <VoiceStatusCard />
        </AuiIf>

        <ThreadPrimitive.Messages>
          {() => <ThreadMessage />}
        </ThreadPrimitive.Messages>
      </ThreadPrimitive.Viewport>

      <div className="relative flex flex-col gap-2 px-3 pt-3 pb-2">
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <QuickActionChips />
        </AuiIf>
        <Composer />
      </div>
    </ThreadPrimitive.Root>
  );
};

const ThreadMessage: FC = () => {
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);
  if (isEditing) return <EditComposer />;
  return role === "user" ? <UserMessage /> : <AssistantMessage />;
};

const LeaseCalculatorRunning: FC = () => {
  const t = useT();
  return (
    <div className="my-3 rounded-2xl border border-blue-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="size-2 animate-pulse rounded-full bg-blue-500" />
        <p className="font-semibold text-neutral-900 text-sm">
          {t("leaseCalc.calculating")}
        </p>
      </div>
    </div>
  );
};

const ToolPartRenderer = (part: ToolCallMessagePartProps) => {
  if (
    part.toolName === "FindDealerLocations" ||
    part.toolName === "ListDealerLocations"
  ) {
    return <DealerLocationToolCard {...part} />;
  }

  if (
    part.toolName === "SearchVehicles" ||
    part.toolName === "GetVehicleDetails"
  ) {
    return <VehicleStockCard {...part} />;
  }

  if (part.toolName === "ProposeSlots") {
    if (part.status?.type !== "complete" || !part.result) {
      console.warn("[ToolRenderer] ProposeSlots fallback: status or result missing", { status: part.status?.type, hasResult: !!part.result });
      return <ToolFallback {...part} />;
    }
    let r: Record<string, unknown>;
    if (typeof part.result === "string") {
      try {
        r = JSON.parse(part.result);
      } catch {
        console.error("[ToolRenderer] ProposeSlots result is a non-JSON string", { result: part.result });
        return <ToolFallback {...part} />;
      }
    } else {
      r = part.result as Record<string, unknown>;
    }
    if (r.status !== "found" || !Array.isArray(r.slots)) {
      console.warn("[ToolRenderer] ProposeSlots fallback: unexpected result shape", { status: r.status, slotsIsArray: Array.isArray(r.slots) });
      return <ToolFallback {...part} />;
    }
    console.log("[ToolRenderer] ProposeSlots rendering SlotPicker", { slotCount: r.slots.length });
    return (
      <SlotPicker
        data={
          {
            slots: r.slots,
            dealer: r.dealer,
            acceptanceType: r.acceptanceType,
            vehicle: r.vehicle ?? null,
            prefill: r.prefill ?? null,
          } as SlotPickerPayload
        }
      />
    );
  }

  if (part.toolName === "ConfirmBooking") {
    if (part.status?.type !== "complete" || !part.result) {
      return <ToolFallback {...part} />;
    }
    const r = part.result as Record<string, unknown>;
    if (!r.status || !r.appointmentId) {
      return <ToolFallback {...part} />;
    }
    return <BookingConfirmationCard data={r as unknown as BookingConfirmationPayload} />;
  }

  if (part.toolName === "CalculateLease") {
    if (part.status?.type === "running") {
      return <LeaseCalculatorRunning />;
    }
    if (part.status?.type !== "complete" || !part.result) {
      return <ToolFallback {...part} />;
    }
    let r: LeaseCalculatorResult;
    if (typeof part.result === "string") {
      try {
        r = JSON.parse(part.result) as LeaseCalculatorResult;
      } catch {
        return <ToolFallback {...part} />;
      }
    } else {
      r = part.result as LeaseCalculatorResult;
    }
    if (r.status !== "ok") {
      return <ToolFallback {...part} />;
    }
    return <LeaseCalculatorCard data={r} />;
  }

  if (part.toolName === "ClassifyCustomerTypology") {
    if (part.status?.type !== "complete" || !part.result) {
      return <ToolFallback {...part} />;
    }
    const r = part.result as TypologyResult;
    if (!r.typology) {
      return <ToolFallback {...part} />;
    }
    return <TypologyCard data={r} />;
  }

  if (part.toolName === "EstimateFinancingEligibility") {
    if (part.status?.type !== "complete" || !part.result) {
      return <ToolFallback {...part} />;
    }
    let r: FinancingEligibilityResult;
    if (typeof part.result === "string") {
      try {
        r = JSON.parse(part.result) as FinancingEligibilityResult;
      } catch {
        return <ToolFallback {...part} />;
      }
    } else {
      r = part.result as FinancingEligibilityResult;
    }
    if (r.status !== "awaiting-answers" && r.status !== "ok") {
      return <ToolFallback {...part} />;
    }
    return <FinancingEligibilityCard data={r} />;
  }

  return <ToolFallback {...part} />;
};

const ThreadWelcome: FC = () => {
  const t = useT();
  return (
    <div className="aui-thread-welcome-root flex w-full max-w-(--thread-max-width) flex-col px-1 pt-2 text-left">
      <div className="fade-in slide-in-from-bottom-1 flex animate-in items-start gap-3 fill-mode-both duration-200">
        <img
          src="/assets/chat/agent.svg"
          alt=""
          aria-hidden
          className="mt-0.5 size-8 shrink-0"
        />
        <div className="rounded-2xl bg-white px-4 py-3 text-neutral-800 text-sm leading-relaxed shadow-sm">
          {t("thread.greeting")}
        </div>
      </div>
    </div>
  );
};

const QuickActionChips: FC = () => {
  const t = useT();
  const runtime = useThreadRuntime({ optional: true });
  const isRunning = useAuiState((s) => s.thread.isRunning);

  const chips = [
    {
      label: t("thread.quickAction.testDrive"),
      prompt: t("thread.quickAction.testDrive.prompt"),
      Icon: CarIcon,
    },
    {
      label: t("thread.quickAction.offers"),
      prompt: t("thread.quickAction.offers.prompt"),
      Icon: TagIcon,
    },
    {
      label: t("thread.quickAction.showroom"),
      prompt: t("thread.quickAction.showroom.prompt"),
      Icon: StoreIcon,
    },
  ];

  const handleClick = (prompt: string) => {
    if (!runtime || isRunning) return;
    runtime.append({
      role: "user",
      content: [{ type: "text", text: prompt }],
    });
  };

  return (
    <div className="flex flex-nowrap items-center justify-center gap-1.5">
      {chips.map(({ label, prompt, Icon }) => (
        <button
          key={label}
          type="button"
          onClick={() => handleClick(prompt)}
          disabled={isRunning}
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-neutral-700 shadow-sm hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon className="size-3 shrink-0" />
          <span className="text-[12px]">{label}</span>
        </button>
      ))}
    </div>
  );
};

const Composer: FC = () => {
  const t = useT();
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // Auto-dismiss transient voice errors (e.g. "No speech detected") after a
  // few seconds so they don't linger across the rest of the session.
  useEffect(() => {
    if (!voiceError) return;
    const id = setTimeout(() => setVoiceError(null), 4000);
    return () => clearTimeout(id);
  }, [voiceError]);

  return (
    <div className="aui-composer-shell p-2">
      <ComposerPrimitive.Root className="aui-composer-root relative flex w-full items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1">
        <PlusIcon className="size-4 shrink-0 text-neutral-400" />
        <ComposerPrimitive.Input
          placeholder={t("thread.composer.placeholder")}
          className="aui-composer-input min-h-9 max-h-32 w-full flex-1 resize-none bg-transparent px-1 py-2 text-sm placeholder:text-[#a3a3a3]"
          style={{ border: "none", color: "#404040" }}
          rows={1}
          autoFocus
          aria-label={t("thread.composer.ariaLabel")}
        />
        <ComposerAction onVoiceError={setVoiceError} />
      </ComposerPrimitive.Root>
      {voiceError ? (
        <p className="mt-2 px-2 text-[12px] text-red-600">{voiceError}</p>
      ) : null}
    </div>
  );
};

const VoiceStatusCard: FC = () => {
  const t = useT();
  const isCapturing = useVoiceStore((s) => s.isCapturing);
  const isVoiceActive = useVoiceStore((s) => s.isVoiceActive);
  const isTranscribing = useVoiceStore((s) => s.isTranscribing);
  // Surface the card while the recorder is *actually* capturing (covers
  // mid-utterance pauses up to the silence timeout), or while VAD
  // momentarily detects voice, or while the backend STT is in flight. The
  // silent wait-for-voice phase deliberately stays hidden so the card
  // doesn't linger between utterances in an AutoListen session.
  const visible = isCapturing || isVoiceActive || isTranscribing;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible) {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [visible]);

  if (!visible) return null;
  const label = isTranscribing
    ? t("thread.voice.transcribing")
    : t("thread.voice.listening");
  return (
    <div
      ref={ref}
      className="mx-auto my-3 w-full max-w-(--thread-max-width) flex justify-center"
    >
      <div
        role="status"
        aria-live="polite"
        className="fade-in slide-in-from-bottom-1 flex animate-in items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-neutral-600 text-sm shadow-sm"
      >
        <Loader2Icon className="size-4 animate-spin text-blue-500" />
        <span>{label}</span>
      </div>
    </div>
  );
};

const ComposerAction: FC<{
  onVoiceError: (message: string | null) => void;
}> = ({ onVoiceError }) => {
  const t = useT();
  const isRecording = useVoiceStore((s) => s.isRecording);
  return (
    <div className="aui-composer-action-wrapper flex shrink-0 items-center gap-1">
      <MicButton onVoiceError={onVoiceError} />
      {/* Auto-listen re-arms the mic after each utterance, so it is meaningless
          without dictation. Where SpeechRecognition is missing (Firefox) MicButton
          renders nothing, and this toggle would otherwise sit there doing nothing.
          Auto-speak stays: playback uses speechSynthesis and works everywhere. */}
      {SUPPORTS_WEB_SPEECH && <AutoListenToggle />}
      <AutoSpeakToggle />
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip={t("thread.composer.send")}
            side="top"
            type="button"
            variant="ghost"
            size="icon"
            disabled={isRecording}
            className="aui-composer-send size-8 rounded-full border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-100"
            aria-label={t("thread.composer.send")}
          >
            <ArrowUpRightIcon className="aui-composer-send-icon size-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="aui-composer-cancel size-9 rounded-full bg-neutral-200 text-neutral-600 hover:bg-neutral-300"
            aria-label={t("thread.composer.stop")}
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

type MicButtonProps = { onVoiceError: (message: string | null) => void };

// Capability check is stable per browser; compute once at module scope.
// Safe because Vite is CSR-only and `isSupported` already handles a missing
// window in `@assistant-ui/core/dist/adapters/speech.js:45-49`.
const SUPPORTS_WEB_SPEECH =
  typeof window !== "undefined" && WebSpeechDictationAdapter.isSupported();

const MicButton: FC<MicButtonProps> = ({ onVoiceError }) => {
  // Browser-native dictation only (Chrome / Edge / Safari). The standalone build
  // has no server-side STT, so where SpeechRecognition is missing (Firefox today)
  // the mic is simply not offered. The decision is made here from feature
  // detection; /health reports the same fact for operators but is not consulted.
  if (!SUPPORTS_WEB_SPEECH) {
    return null;
  }
  return <DictationMicButton onVoiceError={onVoiceError} />;
};

type MicVisualProps = {
  isActive: boolean;
  isBusy: boolean;
  tooltip: string;
  onClick: () => void;
};

// Shared button chrome so both branches stay visually identical.
const MicVisual: FC<MicVisualProps> = ({ isActive, isBusy, tooltip, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isBusy}
    aria-label={tooltip}
    aria-pressed={isActive}
    title={tooltip}
    className={cn(
      "flex size-8 items-center justify-center rounded-full transition-colors",
      isActive
        ? "bg-red-100 text-red-600 hover:bg-red-200"
        : "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600",
      isBusy && "opacity-60",
    )}
  >
    {isActive ? (
      <SquareIcon className="size-3 fill-current" />
    ) : (
      <MicIcon className="size-4" />
    )}
  </button>
);

const DictationMicButton: FC<MicButtonProps> = ({ onVoiceError }) => {
  const t = useT();
  const runtime = useThreadRuntime({ optional: true });
  const isDictating = useAuiState((s) => s.composer.dictation != null);
  const threadRunning = useAuiState((s) => s.thread.isRunning);
  const setRecording = useVoiceStore((s) => s.setRecording);
  const setCapturing = useVoiceStore((s) => s.setCapturing);
  const isSessionActive = useVoiceStore((s) => s.isSessionActive);
  const setSessionActive = useVoiceStore((s) => s.setSessionActive);
  const isSpeaking = useVoiceStore((s) => s.isSpeaking);
  const autoListenEnabled = useVoiceStore((s) => s.autoListenEnabled);
  const setAutoListenEnabled = useVoiceStore((s) => s.setAutoListenEnabled);
  const pendingAutoSendRef = useRef(false);
  const pendingRestartRef = useRef(false);
  const sessionActiveRef = useRef(isSessionActive);
  sessionActiveRef.current = isSessionActive;
  const prevSessionActiveRef = useRef(isSessionActive);
  const [starting, setStarting] = useState(false);
  // Tracks the most recent moment isSpeaking was true. Used by the re-arm
  // gate so a brief flicker (native TTS firing "end" instantly when no
  // matching voice is installed) can't sneak the mic back on.
  const lastSpeakingTrueAtRef = useRef<number>(0);
  useEffect(() => {
    if (isSpeaking) {
      lastSpeakingTrueAtRef.current = performance.now();
    }
  }, [isSpeaking]);

  // Mirror the actual dictation/session state into the store so isRecording
  // reflects reality without scattered manual flips. Becomes true the moment
  // the user clicks (starting=true, before getUserMedia even resolves) and
  // stays true throughout a session — including the brief idle gap between
  // re-armed utterances. Cleanup on unmount avoids stuck flags when the user
  // flips the cloud/native path mid-session.
  useEffect(() => {
    setRecording(isDictating || isSessionActive || starting);
    return () => setRecording(false);
  }, [isDictating, isSessionActive, starting, setRecording]);

  // Capturing flag — drives the VoiceStatusCard. For native dictation we
  // treat the whole isDictating window as "capturing" since Web Speech
  // doesn't expose a separate VAD-trigger event.
  useEffect(() => {
    setCapturing(isDictating);
    return () => setCapturing(false);
  }, [isDictating, setCapturing]);

  // Auto-send when dictation ends (user stop, silence end-of-utterance, or
  // recognition error). Re-arms the session for the next utterance if still on.
  useEffect(() => {
    if (isDictating) return;
    if (!pendingAutoSendRef.current) return;
    pendingAutoSendRef.current = false;
    const composer = runtime?.composer;
    const text = composer?.getState().text?.trim();
    if (text) {
      composer?.setText("");
      console.log("[DictationMicButton] auto-send on dictation end");
      void runtime?.append({
        role: "user",
        content: [{ type: "text", text }],
      });
    }
    if (sessionActiveRef.current) {
      pendingRestartRef.current = true;
    }
  }, [isDictating, runtime]);

  // Re-arm dictation when all gates are idle and a restart is pending. Two
  // delays at play, same as RecorderMicButton:
  //   - BASE_DELAY_MS: settle window so AutoReadPlayer's setSpeaking(true) on
  //     the same commit can propagate before we start.
  //   - SPEAKING_COOLDOWN_MS: floor on time-since-last-speaking, so a TTS
  //     true→false flicker can't sneak the mic back on prematurely.
  // Cleanup cancels the pending start if any gate re-asserts.
  useEffect(() => {
    if (!pendingRestartRef.current) return;
    if (!isSessionActive) return;
    if (threadRunning) return;
    if (isSpeaking) return;
    if (isDictating) return;
    const composer = runtime?.composer;
    if (!composer) return;
    const BASE_DELAY_MS = 300;
    const SPEAKING_COOLDOWN_MS = 1500;
    const sinceSpeaking = performance.now() - lastSpeakingTrueAtRef.current;
    const wait = Math.max(BASE_DELAY_MS, SPEAKING_COOLDOWN_MS - sinceSpeaking);
    const timer = setTimeout(() => {
      pendingRestartRef.current = false;
      pendingAutoSendRef.current = true;
      console.info(
        `[Voice] STT using native Web Speech (re-arm, waited ${wait}ms)`,
      );
      composer.startDictation();
    }, wait);
    return () => clearTimeout(timer);
  }, [isSessionActive, threadRunning, isSpeaking, isDictating, runtime]);

  // Toggle-armed start: when AutoListen turns sessionActive on without going
  // through this button's handleClick (e.g. the user just clicked the
  // AutoListen toggle), kick off the first dictation cycle. Detect via the
  // false → true rising edge on isSessionActive; skip when handleClick is
  // already in flight (starting=true) or has already started (isDictating).
  useEffect(() => {
    const prev = prevSessionActiveRef.current;
    prevSessionActiveRef.current = isSessionActive;
    if (!isSessionActive || prev) return;
    if (starting || isDictating) return;
    if (threadRunning || isSpeaking) return;
    const composer = runtime?.composer;
    if (!composer) return;
    void (async () => {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        onVoiceError(t("thread.composer.voiceUnsupported"));
        setSessionActive(false);
        setAutoListenEnabled(false);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        for (const track of stream.getTracks()) track.stop();
      } catch (err) {
        const name = err instanceof DOMException ? err.name : "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          onVoiceError(t("thread.composer.voicePermissionDenied"));
        } else {
          console.warn("[DictationMicButton] toggle-armed probe failed", err);
          onVoiceError(t("thread.composer.voiceUnavailable"));
        }
        setSessionActive(false);
        setAutoListenEnabled(false);
        return;
      }
      pendingAutoSendRef.current = true;
      console.info("[Voice] STT using native Web Speech (toggle-armed)");
      composer.startDictation();
    })();
  }, [
    isSessionActive,
    isDictating,
    starting,
    threadRunning,
    isSpeaking,
    runtime,
    onVoiceError,
    setSessionActive,
    setAutoListenEnabled,
    t,
  ]);

  const handleClick = useCallback(async () => {
    // Cancel anything in flight — session mode or a single-shot dictation.
    // isRecording mirrors hook state so no manual reset. Stopping the mic
    // also disables AutoListen so it doesn't immediately re-arm.
    if (isSessionActive || isDictating) {
      if (isSessionActive) {
        setSessionActive(false);
        sessionActiveRef.current = false;
      }
      setAutoListenEnabled(false);
      pendingRestartRef.current = false;
      pendingAutoSendRef.current = false;
      if (isDictating) runtime?.composer?.stopDictation();
      return;
    }

    onVoiceError(null);

    if (typeof window !== "undefined" && !window.isSecureContext) {
      onVoiceError(t("thread.composer.voiceRequiresSecureContext"));
      return;
    }
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      onVoiceError(t("thread.composer.voiceUnsupported"));
      return;
    }

    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
    } catch (err) {
      setStarting(false);
      const name = err instanceof DOMException ? err.name : "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        onVoiceError(t("thread.composer.voicePermissionDenied"));
      } else {
        console.warn("[DictationMicButton] mic probe failed", err);
        onVoiceError(t("thread.composer.voiceUnavailable"));
      }
      return;
    }

    const composer = runtime?.composer;
    if (!composer) {
      setStarting(false);
      console.warn("[DictationMicButton] no composer runtime");
      onVoiceError(t("thread.voice.error"));
      return;
    }
    if (autoListenEnabled) {
      sessionActiveRef.current = true;
      setSessionActive(true);
    }
    pendingAutoSendRef.current = true;
    console.info(
      `[Voice] STT using native Web Speech (${autoListenEnabled ? "session" : "single-shot"})`,
    );
    composer.startDictation();
    setStarting(false);
  }, [autoListenEnabled, isSessionActive, isDictating, onVoiceError, runtime, setAutoListenEnabled, setSessionActive, t]);

  const active = isSessionActive || isDictating;
  const tooltip = active ? t("thread.voice.stop") : t("thread.voice.start");

  return (
    <MicVisual
      isActive={active}
      isBusy={starting}
      tooltip={tooltip}
      onClick={handleClick}
    />
  );
};

const AutoListenToggle: FC = () => {
  const t = useT();
  const enabled = useVoiceStore((s) => s.autoListenEnabled);
  const setEnabled = useVoiceStore((s) => s.setAutoListenEnabled);
  const setSessionActive = useVoiceStore((s) => s.setSessionActive);
  const label = enabled
    ? t("thread.voice.autoListenOn")
    : t("thread.voice.autoListenOff");
  const handleClick = () => {
    const next = !enabled;
    setEnabled(next);
    // Flipping the toggle drives the mic directly:
    //  - ON  → arm a session; the active mic button (Recorder via its
    //          gate-driven cycle, Dictation via its rising-edge effect)
    //          picks it up and starts capturing immediately.
    //  - OFF → end the session so the mic stops re-arming.
    setSessionActive(next);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      aria-pressed={enabled}
      title={label}
      className={cn(
        "flex size-8 items-center justify-center rounded-full transition-colors",
        enabled
          ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
          : "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600",
      )}
    >
      {enabled ? (
        <EarIcon className="size-4" />
      ) : (
        <EarOffIcon className="size-4" />
      )}
    </button>
  );
};

const AutoSpeakToggle: FC = () => {
  const t = useT();
  const enabled = useVoiceStore((s) => s.autoReadEnabled);
  const setEnabled = useVoiceStore((s) => s.setAutoReadEnabled);
  const label = enabled
    ? t("thread.voice.autoReadOn")
    : t("thread.voice.autoReadOff");
  return (
    <button
      type="button"
      onClick={() => setEnabled(!enabled)}
      aria-label={label}
      aria-pressed={enabled}
      title={label}
      className={cn(
        "flex size-8 items-center justify-center rounded-full transition-colors",
        enabled
          ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
          : "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600",
      )}
    >
      {enabled ? (
        <Volume2Icon className="size-4" />
      ) : (
        <VolumeXIcon className="size-4" />
      )}
    </button>
  );
};

const AutoReadPlayer: FC = () => {
  const enabled = useVoiceStore((s) => s.autoReadEnabled);
  const setSpeaking = useVoiceStore((s) => s.setSpeaking);
  const locale = useLocale();
  const messageId = useAuiState((s) => s.message.id);
  const statusType = useAuiState((s) =>
    s.message.role === "assistant" ? s.message.status?.type : undefined,
  );
  const text = useAuiState((s) => messageToPlainText(s.message as unknown));
  const playedRef = useRef<Set<string>>(new Set());
  const handleRef = useRef<TtsHandle | null>(null);

  useEffect(() => {
    return () => {
      handleRef.current?.stop();
      handleRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (statusType !== "complete") return;
    if (!messageId || playedRef.current.has(messageId)) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    playedRef.current.add(messageId);
    handleRef.current?.stop();
    const handle = speakText(trimmed, toBcp47(locale));
    handleRef.current = handle;
    setSpeaking(true);
    void handle.ended.finally(() => {
      if (handleRef.current === handle) setSpeaking(false);
    });
  }, [enabled, statusType, messageId, text, locale, setSpeaking]);

  return null;
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const TypingDots: FC = () => (
  <span className="dc-streaming" aria-label="Assistant is typing">
    <span />
    <span />
    <span />
  </span>
);

const AssistantMessage: FC = () => {
  const isLast = useAuiState((s) => s.message.isLast);
  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <AutoReadPlayer />
      <div className="flex items-start gap-3">
        <img
          src="/assets/chat/agent.svg"
          alt=""
          aria-hidden
          className="mt-0.5 size-8 shrink-0"
        />
        <div className="aui-assistant-message-content wrap-break-word rounded-2xl bg-white px-4 py-3 text-neutral-800 text-sm leading-relaxed shadow-sm empty:hidden">
          <MessagePrimitive.Parts>
            {({ part }) => {
              if (part.type === "text") return <MarkdownText />;
              return null;
            }}
          </MessagePrimitive.Parts>
          <AuiIf condition={(s) => s.message.status?.type === "running"}>
            <TypingDots />
          </AuiIf>
          <MessageError />
        </div>
      </div>
      <div className="aui-assistant-message-tools mt-2 empty:hidden">
        <MessagePrimitive.Parts>
          {({ part }) => {
            if (part.type === "tool-call")
              return part.toolUI ?? <ToolPartRenderer {...part} />;
            return null;
          }}
        </MessagePrimitive.Parts>
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-11 flex min-h-6 items-center">
        <BranchPicker />
        <AssistantActionBar />
      </div>
      {isLast ? <VoiceStatusCard /> : null}
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const t = useT();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={t("thread.action.copy")}>
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <SpeakButton />
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  const isLast = useAuiState((s) => s.message.isLast);
  return (
    // Flex column rather than the previous two-track grid. That grid reserved a
    // fixed 72px gutter for the edit button and gave the remainder to a greedy
    // 1fr spacer, which inside the ~420px chat panel squeezed the bubble to about
    // a quarter of the row -- long words broke mid-word. It also referenced
    // --thread-max-width, a variable this app never defines.
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 flex w-full animate-in flex-col items-end gap-y-2 px-3 py-3 duration-150"
      data-role="user"
    >
      {/* Cap at 85% so the bubble still reads as "mine" (right-aligned, indented
          on the left) without wrapping more than it has to. */}
      <div className="aui-user-message-content-wrapper relative min-w-0 max-w-[85%]">
        <div className="aui-user-message-content wrap-break-word peer rounded-2xl border border-white bg-white px-4 py-2.5 text-neutral-800 text-sm shadow-sm empty:hidden">
          <MessagePrimitive.Parts />
        </div>
        <div className="aui-user-action-bar-wrapper absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 pr-2 peer-empty:hidden">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker className="aui-user-branch-picker -mr-1 justify-end" />
      {isLast ? (
        <div className="w-full">
          <VoiceStatusCard />
        </div>
      ) : null}
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  const t = useT();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip={t("thread.action.edit")} className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  const t = useT();
  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              {t("thread.edit.cancel")}
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">{t("thread.edit.update")}</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  const t = useT();
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t("thread.action.previous")}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t("thread.action.next")}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
